/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'

// Components
import {
    Breadcrumb as ChakraBreadcrumb,
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    // Hooks
    useStyleConfig,
} from '@chakra-ui/react'

// Icons
import LineIcon from '../icons/generic/line'

// Others
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'

/**
 * A simplification of the Chakra `Breadcrumb` component for our project needs. Given
 * a list of categories, display a breadcrumb and it's items.
 */
const Breadcrumb = ({categories, homepage = false, ...rest}) => {
    const styles = useStyleConfig('Breadcrumb')
    const {buildUrl} = useMultiSite()
    const {buildLocalizedUrl} = useMulticountryUtils()

    return (
        <ChakraBreadcrumb {...styles.container} {...rest} separator={<LineIcon {...styles.icon} />}>
            {categories?.map((category, index) => {
                //check the last item of the breadcrumbs for a different style
                const linkStyle = index === categories.length - 1 ? styles.lastLink : styles.link

                const url = homepage
                    ? buildLocalizedUrl('/', buildUrl)
                    : buildLocalizedUrl(category?.c_slugUrl)

                return (
                    <ChakraBreadcrumbItem
                        key={category.id}
                        data-testid="sf-crumb-item"
                        {...styles.item}
                    >
                        <ChakraBreadcrumbLink
                            as={RouteLink}
                            to={url}
                            //applying directly the style on the breadcrumb items won't work with ternary operator
                            sx={linkStyle}
                        >
                            {category.name}
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                )
            })}
        </ChakraBreadcrumb>
    )
}

Breadcrumb.displayName = 'Breadcrumb'

Breadcrumb.propTypes = {
    /**
     * The categories to be displayed in this breadcrumb.
     */
    categories: PropTypes.array,
    homepage: PropTypes.bool,
}

export default Breadcrumb
